import qs from 'query-string'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { Alert } from 'antd'
import { SLIDE_QUERY_PARAM } from '@Admin/AdminQuotingPage/constants'
import DfmCategorySection from '@Builder/QuoteDetailPage/FilesTab/ImQuoteTabSections/ManufacturabilityFeedbackTab/DfmCategorySection'
import ImDfmModal from '@Builder/QuoteDetailPage/FilesTab/ImQuoteTabSections/ManufacturabilityFeedbackTab/ImDfmModal'
import { getUrl } from '@Builder/QuoteDetailPage/FilesTab/ConfigurationModal/logic'
import { DfmCategoryType } from '@Builder/QuoteDetailPage/FilesTab/ImQuoteTabSections/ManufacturabilityFeedbackTab/DfmCategorySection/constants'
import { IM_DFM_URL_PARAM } from '@Builder/QuoteDetailPage/FilesTab/ImQuoteTabSections/constants'
import NotifyFictivAlert from '@Builder/QuoteDetailPage/FilesTab/ImQuoteTabSections/ManufacturabilityFeedbackTab/NotifyFictivAlert'
import { MANUFACTURABILITY_FEEDBACK_TAB } from '@Orders/constants'

import Box from '~/core/Box'
import { DEFAULT_REFRESH_MSG } from '~/lib/flashMsgs'
import { ADMIN_PATH, ORDERS_PATH } from '~/redux-modules/preferences'

import qmrsGroupedByDfmCategoryOdpQuery from './qmrsGroupedByDfmCategoryOdpQuery'
import {
    qmrsGroupedByDfmCategoryOdp_qmrsGroupedByDfmCategory as QmrDfmCategoryType,
    qmrsGroupedByDfmCategoryOdp_qmrsGroupedByDfmCategory_qmrs as QmrType,
} from './__generated__/qmrsGroupedByDfmCategoryOdp'

interface ManufacturabilityFeedbackTabPropsType {
    isImOrder: boolean
    orderId: string
    isHighTouch: boolean
    isNonImDfmEnabled: boolean
}

type ParamsType = {
    orderName: string
    tabs?: string
    userId: string
}

const FEEDBACK_URL_PARAM = 'feedback'

const ManufacturabilityFeedbackTab = (
    props: ManufacturabilityFeedbackTabPropsType,
) => {
    const { orderId, isImOrder, isHighTouch, isNonImDfmEnabled } = props

    const navigate = useNavigate()
    const { pathname } = useLocation()
    const { tabs, orderName, userId } = useParams<ParamsType>()

    const [isModalVisible, setIsModalVisible] = useState(false)

    const { data, refetch } = useQuery(qmrsGroupedByDfmCategoryOdpQuery, {
        variables: { orderId },
        skip: !orderId,
    })

    useEffect(() => {
        if (tabs === IM_DFM_URL_PARAM) {
            setIsModalVisible(true)
        }
    }, [tabs])

    const onClickViewDfmModal = (qmrId: string, dfmId = '') => {
        const path = getUrl(pathname, FEEDBACK_URL_PARAM)
        const updatedPathname = `${path}/qmr/${qmrId}/imDfmFeedback`
        const hash = orderId ? `${MANUFACTURABILITY_FEEDBACK_TAB}` : ''

        navigate(
            {
                pathname: updatedPathname,
                search: qs.stringify({
                    [SLIDE_QUERY_PARAM]: dfmId,
                }),
                hash,
            },
            { replace: true },
        )

        setIsModalVisible(true)
    }

    const isOBO = Boolean(userId)

    const onCancel = async () => {
        await refetch()
        setIsModalVisible(false)
        const orderDetailsPageUrl = isOBO
            ? `${ADMIN_PATH}/user/${userId}${ORDERS_PATH}/${orderName}#${MANUFACTURABILITY_FEEDBACK_TAB}`
            : `${ORDERS_PATH}/${orderName}#${MANUFACTURABILITY_FEEDBACK_TAB}`

        navigate(orderDetailsPageUrl)
    }

    const syncedOrderId = orderId.includes('erp')

    if (orderId && !syncedOrderId) {
        return <Alert message={DEFAULT_REFRESH_MSG} type="error" showIcon />
    }

    const hasDfmFeedback = data?.qmrsGroupedByDfmCategory?.some(
        (qmrsGroupedByDfmCategory: { qmrs: QmrType[] }) => {
            return qmrsGroupedByDfmCategory?.qmrs?.some(
                qmrs => qmrs?.dfmFeedback?.length !== 0,
            )
        },
    )

    return (
        <Box direction="col">
            {hasDfmFeedback && <NotifyFictivAlert orderId={orderId} />}
            {data?.qmrsGroupedByDfmCategory?.map(
                ({ category, qmrs }: QmrDfmCategoryType) =>
                    Boolean(qmrs?.length) && (
                        <DfmCategorySection
                            category={category as DfmCategoryType}
                            key={category}
                            onClickViewDfmModal={onClickViewDfmModal}
                            qmrs={qmrs}
                            isHighTouch={isHighTouch}
                            isNonImDfmEnabled={isNonImDfmEnabled}
                        />
                    ),
            )}
            {isModalVisible && (
                <ImDfmModal
                    onCancel={onCancel}
                    setIsModalVisible={setIsModalVisible}
                    visible={isModalVisible}
                    isImQuoteType={isImOrder}
                    orderId={orderId}
                />
            )}
        </Box>
    )
}

export default ManufacturabilityFeedbackTab
